import { Box, Typography, useTheme } from '@mui/material';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useMemo } from 'react';
import { ExpenseStatsItem } from '../../../../types/dashboard';
import { formatCurrencyShort } from '../../../../../shared/utils';
import { CustomChip } from '../../../../../layout/styledcomponents/CustomChip';
import GenericTable from '../../../../shared/GenericTable';
import {
    getPaidStatusChipTone,
    getReportChipSx,
} from '../../reportChartConfig';
import { dashboardItemsTableDefaults } from '../../dashboardReportTableConfig';

type ExpenseClaimedExpensesListProps = {
    expenses: ExpenseStatsItem[];
};

export default function ExpenseClaimedExpensesList({
    expenses,
}: ExpenseClaimedExpensesListProps) {
    const theme = useTheme();

    const columns: MRT_ColumnDef<ExpenseStatsItem>[] = useMemo(
        () => [
            {
                accessorKey: 'id',
                header: 'Expense Id',
                size: 110,
                Cell: ({ cell }) => `#${cell.getValue<number>()}`,
            },
            {
                accessorKey: 'name',
                header: 'Expense',
                size: 140,
            },
            {
                accessorKey: 'date',
                header: 'Date',
                size: 120,
                sortingFn: 'datetime',
                Cell: ({ cell }) =>
                    moment(cell.getValue<string>()).format('DD/MM/YYYY'),
            },
            {
                id: 'reservation',
                accessorFn: (row) =>
                    `#${row.bookingId} - ${row.module?.name ?? ''}`,
                header: 'Reservation',
                size: 200,
            },
            {
                id: 'guestName',
                accessorFn: (row) => row.guest?.name ?? '—',
                header: 'Guest',
                size: 140,
            },
            {
                id: 'ownerName',
                accessorFn: (row) => row.owner?.name ?? '—',
                header: 'Host',
                size: 140,
            },
            {
                accessorKey: 'amount',
                header: 'Amount (inc GST)',
                size: 150,
                Cell: ({ cell }) =>
                    formatCurrencyShort(cell.getValue<number>()),
            },
            {
                id: 'status',
                accessorFn: (row) => (row.hostPaidAt ? 'Paid' : 'Unpaid'),
                header: 'Status',
                size: 110,
                Cell: ({ cell }) => {
                    const isPaid = cell.getValue<string>() === 'Paid';
                    return (
                        <CustomChip
                            label={cell.getValue<string>()}
                            sx={getReportChipSx(
                                getPaidStatusChipTone(isPaid),
                                theme,
                            )}
                            variant='outlined'
                        />
                    );
                },
            },
        ],
        [theme],
    );

    const rows = expenses ?? [];

    if (!rows.length) {
        return (
            <Typography sx={{ mt: 4 }} variant='body2'>
                No individual expense claims found for this selection.
            </Typography>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <GenericTable
                {...dashboardItemsTableDefaults}
                columns={columns}
                data={rows}
                tableName='Expense Claimed Report'
                paginationItemLabel='expenses'
            />
        </Box>
    );
}
